/**
 * First-party analytics client.
 *
 * The payload shape is three allowlisted strings. There is no room id, no title, no
 * amount, no token, no URL and no free-text field — not because the server would reject
 * them (it does), but because there is nowhere in this function to put them.
 *
 * See ANALYTICS.md for the full event list and the explicit prohibitions.
 */

export type AnalyticsEventName =
  | 'landing_view'
  | 'create_start'
  | 'room_created'
  | 'share_action'
  | 'recipient_open'
  | 'response_submit'
  | 'response_edit'
  | 'room_revealed'
  | 'result_view'
  | 'participant_to_creator'
  | 'second_room_create'
  | 'error_shown';

type RouteClass = 'public' | 'private';
type ReferrerClass = 'direct' | 'search' | 'social' | 'internal' | 'other';

const SEARCH_HOSTS = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|brave|startpage|qwant)\./i;
const SOCIAL_HOSTS =
  /(^|\.)(whatsapp|facebook|instagram|t|telegram|x|twitter|reddit|linkedin|tiktok|pinterest|discord|messenger|snapchat)\.(com|me|co|org)$/i;

/**
 * Classify the referrer into one of five buckets.
 * Only the bucket is ever transmitted — never the referring URL.
 */
function referrerClass(): ReferrerClass {
  try {
    const referrer = document.referrer;
    if (!referrer) return 'direct';

    const host = new URL(referrer).hostname;
    if (host === window.location.hostname) return 'internal';
    if (SEARCH_HOSTS.test(host)) return 'search';
    if (SOCIAL_HOSTS.test(host)) return 'social';
    return 'other';
  } catch {
    return 'direct';
  }
}

let queueBlocked = false;

/**
 * Send one event. Fire-and-forget: analytics must never delay or break a user action,
 * and a failure is simply dropped.
 */
export function track(event: AnalyticsEventName, routeClass: RouteClass, bucket = '-'): void {
  if (queueBlocked) return;

  const payload = JSON.stringify({
    event,
    routeClass,
    // A private route contributes no referrer information at all; the server enforces
    // this too, so both ends of the wire agree.
    referrerClass: routeClass === 'private' ? 'internal' : referrerClass(),
    bucket,
  });

  try {
    void fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      credentials: 'omit',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // If the endpoint is unreachable or blocked by an extension, stop trying.
    queueBlocked = true;
  }
}
